// Feature 13: Action Priority Badge Component

import React from 'react';
import { Chip, Tooltip, Box } from '@mui/material';
import {
  PriorityHigh,
  Warning,
  TrendingUp,
  TrendingDown,
} from '@mui/icons-material';
import { ActionPriority } from '../../types/actionQueue.types';

interface ActionPriorityBadgeProps {
  priority: ActionPriority | number | string; // Support numeric priority from ETL (1=critical, 2=high, 3=medium, 4=low)
  score: number; // 0-100
  compact?: boolean;
}

const ActionPriorityBadge: React.FC<ActionPriorityBadgeProps> = ({
  priority,
  score,
  compact = false,
}) => {
  // Normalize priority - handle both string values and numeric values from ETL
  const normalizedPriority = (() => {
    if (typeof priority === 'number') {
      // ETL returns: 1 = highest priority, 3 = lowest priority
      if (priority === 1) return 'CRITICAL';
      if (priority === 2) return 'HIGH';
      if (priority === 3) return 'MEDIUM';
      return 'LOW';
    }
    // String priority - normalize to uppercase
    const upperPriority = String(priority).toUpperCase();
    if (['CRITICAL', 'HIGH', 'MEDIUM', 'LOW'].includes(upperPriority)) {
      return upperPriority as ActionPriority;
    }
    return 'MEDIUM'; // Default fallback
  })();

  const getConfig = () => {
    switch (normalizedPriority) {
      case 'CRITICAL':
        return {
          icon: <PriorityHigh fontSize="small" />,
          color: 'error' as const,
          label: compact ? 'Critical' : 'Critical Priority',
          bgColor: 'error.lighter',
        };
      case 'HIGH':
        return {
          icon: <Warning fontSize="small" />,
          color: 'warning' as const,
          label: compact ? 'High' : 'High Priority',
          bgColor: 'warning.lighter',
        };
      case 'MEDIUM':
        return {
          icon: <TrendingUp fontSize="small" />,
          color: 'info' as const,
          label: compact ? 'Medium' : 'Medium Priority',
          bgColor: 'info.lighter',
        };
      case 'LOW':
        return {
          icon: <TrendingDown fontSize="small" />,
          color: 'default' as const,
          label: compact ? 'Low' : 'Low Priority',
          bgColor: 'grey.200',
        };
    }
  };

  const config = getConfig();

  return (
    <Tooltip
      title={
        <Box>
          Priority Score: {score}/100
          <br />
          {score >= 80 && 'Immediate action recommended'}
          {score >= 60 && score < 80 && 'Address within this week'}
          {score >= 40 && score < 60 && 'Schedule for next week'}
          {score < 40 && 'Lower priority, defer if needed'}
        </Box>
      }
    >
      <Chip
        icon={config.icon}
        label={compact ? config.label : `${config.label} (${score})`}
        color={config.color}
        size={compact ? 'small' : 'medium'}
        sx={{
          fontWeight: priority === 'CRITICAL' ? 'bold' : 'normal',
        }}
      />
    </Tooltip>
  );
};

export default ActionPriorityBadge;
